import { Monitor, Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fa } from '@/i18n/fa';
import { useThemeStore, type ThemeMode } from '@/stores/theme.store';

const options: { mode: ThemeMode; icon: typeof Sun; label: string }[] = [
  { mode: 'light', icon: Sun, label: fa.theme.light },
  { mode: 'dark', icon: Moon, label: fa.theme.dark },
  { mode: 'system', icon: Monitor, label: fa.theme.system },
];

/**
 * Three-way light/dark/system segmented control. Persists via the Zustand
 * theme store (`localStorage`) and applies `data-theme` immediately —
 * see `stores/theme.store.ts` for the no-FOUC bootstrap in `index.html`.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  return (
    <div
      role="radiogroup"
      aria-label={fa.theme.toggleAriaLabel}
      className={cn('inline-flex items-center gap-1 rounded-pill border border-border bg-surface p-1', className)}
    >
      {options.map(({ mode: optionMode, icon: Icon, label }) => {
        const isActive = mode === optionMode;
        return (
          <button
            key={optionMode}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={label}
            title={label}
            onClick={() => setMode(optionMode)}
            className={cn(
              'flex size-11 items-center justify-center rounded-pill transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
              isActive
                ? 'bg-primary-600 text-white shadow-soft'
                : 'text-text-subtle hover:bg-surface-raised hover:text-text',
            )}
          >
            <Icon className="size-4" />
          </button>
        );
      })}
    </div>
  );
}
